import {
  CheckIcon,
  Cross2Icon,
  ExclamationTriangleIcon,
  MinusCircledIcon,
  PlusCircledIcon,
} from "@radix-ui/react-icons";
import { Link } from "@tanstack/react-router";

import {
  conditionBlock,
  ConditionBlockCard,
} from "@/components/canvas-editor/condition-blocks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FeedAmendment } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * What an amendment did, with the two buttons that decide whether it sticks.
 *
 * The proposal is ALREADY drawn on the canvas behind this panel. Reviewing a
 * change to a feed means looking at the feed, and a summary next to an unchanged
 * canvas asks the author to imagine the result instead of seeing it. So the
 * canvas shows the amended design and this panel says what to look for; Discard
 * puts the previous design back.
 *
 * The change list is the server's leaf-level diff, not the model's own account
 * of what it did. Those disagree: the model reports its intent, and the diff
 * reports what actually landed. Both are shown, but the diff is the one with
 * the icons and the counts, because it is the one that is true.
 *
 * Each changed condition is DRAWN AS ITS BLOCK -- the same name, colour and
 * fields the canvas gives it. It used to be printed as the leaf itself
 * (`regex_none: ["text", ["political"], true, false]`), which is the wire
 * format and not something anyone authors a feed in. The server's one-line
 * label is still the fallback for a leaf the canvas has no block for, and for
 * an `api` old enough not to send the condition at all.
 */
export function FeedAmendmentProposal({
  amendment,
  onAccept,
  onDiscard,
  className,
}: {
  amendment: FeedAmendment;
  onAccept: () => void;
  onDiscard: () => void;
  className?: string;
}) {
  const { stats, warnings, tools_used: tools } = amendment;
  const changes = amendment.changes.map((change, index) => ({
    ...change,
    key: `${change.kind}:${change.label}:${index}`,
    block: change.condition ? conditionBlock(change.condition) : null,
  }));

  return (
    <div
      className={cn(
        "absolute bottom-4 left-1/2 -translate-x-1/2 z-100 w-[min(44rem,calc(100%-2rem))]",
        "rounded-lg border border-slate-normal bg-slate-1 dark:bg-slatedark-1 shadow-lg",
        "max-h-[60%] flex flex-col",
        className,
      )}
      role="region"
      aria-label="Proposed change"
    >
      <div className="flex items-start justify-between gap-3 px-4 pt-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-slate-dim">
            Proposed change
          </div>
          <p className="text-sm text-slate-normal mt-0.5">
            {amendment.summary || "The design was updated."}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {stats.added > 0 && (
            <Badge variant="success" noIcon>
              +{stats.added}
            </Badge>
          )}
          {stats.removed > 0 && (
            <Badge variant="destructive" noIcon>
              −{stats.removed}
            </Badge>
          )}
          <Badge variant="outline" noIcon>
            {stats.kept} kept
          </Badge>
        </div>
      </div>

      <div className="overflow-y-auto px-4 py-3 flex flex-col gap-3">
        {warnings.map((warning) => (
          <div
            key={warning}
            className="flex gap-2 items-start rounded-md bg-ambera-action border border-ambera-normal text-ambera-normal px-3 py-2 text-xs"
          >
            <ExclamationTriangleIcon className="mt-0.5 shrink-0" />
            <span>{warning}</span>
          </div>
        ))}

        {changes.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {changes.map((change) => (
              <li key={change.key} className="flex gap-2 items-start text-xs">
                {change.kind === "added" ? (
                  <PlusCircledIcon className="mt-1.5 shrink-0 text-green-normal" />
                ) : (
                  <MinusCircledIcon className="mt-1.5 shrink-0 text-red-normal" />
                )}
                {change.block ? (
                  // A removal is dimmed rather than struck through: a block is
                  // a small piece of layout, and a line through it lands across
                  // its fields and makes them harder to read, which is the
                  // opposite of what a removal needs.
                  <div
                    className={cn(
                      "min-w-0 flex-1",
                      change.kind === "removed" && "opacity-70",
                    )}
                  >
                    <ConditionBlockCard block={change.block} />
                  </div>
                ) : (
                  <span
                    className={cn(
                      "font-mono break-all mt-0.5",
                      change.kind === "removed" && "line-through opacity-70",
                    )}
                  >
                    {change.label}
                  </span>
                )}
                {/* Only alongside the fallback line: a block already carries
                    the chip beside its title, the way the canvas does. */}
                {change.is_custom_node && !change.block && (
                  <Badge variant="secondary" noIcon className="shrink-0 mt-1">
                    custom node
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-slate-dim">
            {amendment.structure_changed
              ? "The conditions are the same; only how they are grouped changed."
              : "No conditions changed."}
          </p>
        )}

        {tools.length > 0 && (
          <div className="border-t border-slate-normal pt-3">
            <div className="text-xs uppercase tracking-wide text-slate-dim mb-1.5">
              Custom nodes installed
            </div>
            <ul className="flex flex-col gap-2">
              {tools.map((tool) => (
                <li key={tool.component_id} className="text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      className="font-semibold underline underline-offset-2"
                      to="/app/custom-nodes/$nodeId/view"
                      params={{ nodeId: String(tool.component_id) }}
                      // A new tab: the author is mid-review of a proposal, and
                      // navigating away would discard it.
                      target="_blank"
                    >
                      {tool.title}
                    </Link>
                    {/* Popularity is the cheapest signal that this is not an
                        obscure node the design happened to find. */}
                    <span className="text-slate-dim">
                      {tool.install_count} feed
                      {tool.install_count === 1 ? "" : "s"} use it
                    </span>
                  </div>
                  {tool.reason && (
                    <p className="text-slate-dim mt-0.5">{tool.reason}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-normal">
        <Button variant="outline" size="sm" onPress={onDiscard}>
          <Cross2Icon className="mr-1.5" />
          Discard
        </Button>
        <Button size="sm" onPress={onAccept}>
          <CheckIcon className="mr-1.5" />
          Keep change
        </Button>
      </div>
    </div>
  );
}
